import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { Grid, Tooltip } from "@mui/material";
import { t } from "i18next";
import React, { useEffect, useRef, useState } from "react";
import { Loader } from "../../components/Loader";
import { getUserRoles } from "../../helper/TokenHelper";
import { fruitQuantity, fruitTracability, fruitUnileverPlotQuantity, totalAmount, totalPpunch } from "../../helper/contractHelper";
import { convertToDate, DATE_PATTERN_YYYY_MM_DD_DASH, isBigQuantityNb } from "../../helper/helper";
import { ContractUnilever } from "../../models/contract/ContractUnilever";
import { colorDark, colorPrimary } from "../../rsc/colors/colors";
import { ReactComponent as InfoIcon } from "../../rsc/ic_info.svg";
import { ReactComponent as SortIcon } from "../../rsc/sort.svg";
import { ReportBarSummary } from "./ReportBarSumary";
import { getTraceabilityData } from "../traceability/traceabilityTypes";
import { getContrastingColor } from "../../components/ColorUtils";

const Pagination = 30


type ReportProps = {
    contracts: ContractUnilever[],
    setContracts: React.Dispatch<React.SetStateAction<ContractUnilever[]>>,
    client: ApolloClient<NormalizedCacheObject>,
    sortBy: (column: string) => void
}

export function Report({ contracts, sortBy, client }: ReportProps) {

    const [paginationLoading, setPaginationLoading] = useState(false)

    const nbContractDisplayed = useRef<number>(Pagination)

    const [contractToDisplay, setContractToDisplay] = useState<ContractUnilever[]>(contracts.slice(0, nbContractDisplayed.current))

    const scrollRef = useRef(null);
    const contractsRef = useRef(contracts)

    const isOperator = getUserRoles().findIndex(r => r === 'operator') !== -1

    type ContractItemProps = {
        contract: ContractUnilever
    }

    useEffect(() => {
        contractsRef.current = contracts
        setContractToDisplay(contracts.slice(0, nbContractDisplayed.current))
    }, [contracts])

    const ContractItem = ({ contract }: ContractItemProps) => {
        // const tracabilityTotal = fruitTracability([contract])
        // var tracabilityText: string
        // if (tracabilityTotal) {
        //     tracabilityText = `${(tracabilityTotal * 100).IndoFormat(2)}%`
        // } else {
        //     tracabilityText = "No UL Harvest"
        // }
        const fQuantity = fruitQuantity([contract])
        const fuQuantity = fruitUnileverPlotQuantity([contract])
        const traceability = getTraceabilityData(contract.traceabilityLabel());

        const cellStyle: React.CSSProperties = {
            flex: 1,
            flexShrink: 0
        }

        return (
            <div style={{ display: "flex", flex: "1", gap: "4px", marginTop: "4px", color: colorDark() }}>
                <div style={{ ...cellStyle }} className="table-row-dashboard">
                    <p className="table-header-dashboard-row" style={{ fontWeight: "700", justifyContent: "start" }}>{contract.contractId}</p>
                </div>
                <div style={{ ...cellStyle }} className="table-row-dashboard">
                    <p className="table-header-dashboard-row">{contract.contractType}</p>
                </div>
                <div style={{ ...cellStyle }} className="table-row-dashboard darkColor">
                    <p className="table-header-dashboard-row">{convertToDate(contract.getMarketDay() ?? "", DATE_PATTERN_YYYY_MM_DD_DASH)}</p>
                </div>
                <div style={{ ...cellStyle }} className="table-row-dashboard">
                    <p className="table-header-dashboard-row">{fQuantity.toIndoFormatTons(0)} {isBigQuantityNb(fQuantity) ? 't' : 'kg'}</p>
                </div>
                <div style={{ ...cellStyle }} className="table-row-dashboard">
                    <p className="table-header-dashboard-row">{fuQuantity.toIndoFormatTons(0)} {isBigQuantityNb(fuQuantity) ? 't' : 'kg'}</p>
                </div>
                <div style={{ ...cellStyle }} className="table-row-dashboard">
                    <p className="table-header-dashboard-row">{totalAmount([contract]).IndoFormat()} rp</p>
                </div>
                <div style={{ ...cellStyle }} className="table-row-dashboard">
                    <p className="table-header-dashboard-row">{totalPpunch([contract]).IndoFormat()} rp</p>
                </div>
                {/* <div style={{ ...cellStyle }} className="table-row-dashboard">
                    <p className="table-header-dashboard-row">{`${tracabilityText}`}</p>
                </div> */}
                <div style={{ ...cellStyle }} className="table-row-dashboard pempem-flex-col-center">
                    <div className="pem-label-glass" style={{
                        backgroundColor: traceability.color
                    }}>
                        {traceability.label}
                    </div>
                </div>

            </div>
        )
    }

    type ContractTableProps = {
        contractsParam: ContractUnilever[]
    }

    const ContractTable = ({ contractsParam }: ContractTableProps) => {

        setPaginationLoading(false)

        const cellStyle: React.CSSProperties = {
            flex: 1,
            flexShrink: 0,
            overflowWrap: 'anywhere',
            textAlign:'center'
        }

        return (
            <div ref={scrollRef} style={{ display: "flex", flex: "1", flexDirection: "column", marginTop: "20px" }}>
                <div style={{ display: "flex", flex: "1", gap: "4px" }}>
                    <div style={{ ...cellStyle }} className="table-header-dashboard">
                        <div style={{ display: "flex", flexDirection: "row" }} className="table-header-dashboard-title">
                            <p style={{ flex: "1" }}>{t('contract_number')}</p>
                            <SortIcon style={{ fill: colorPrimary(), cursor: "pointer" }}
                                onClick={() => sortBy("id")} />
                        </div>
                    </div>
                    <div style={{ ...cellStyle }} className="table-header-dashboard">
                        <p className="table-header-dashboard-title" >{t('contract_type')}</p>
                    </div>
                    <div style={{ ...cellStyle }} className="table-header-dashboard">
                        <p className="table-header-dashboard-title">{t('market_day')}</p>
                    </div>
                    <div style={{ ...cellStyle }} className="table-header-dashboard">
                        <p className="table-header-dashboard-title">{t('quantity')}</p>
                    </div>
                    <div style={{ ...cellStyle }} className="table-header-dashboard">
                        <p className="table-header-dashboard-title">{t('unilever_quantity')}</p>
                    </div>
                    <div style={{ ...cellStyle, position: "relative" }} className="table-header-dashboard">
                        <Tooltip title={t('info_report_amount_paid')} style={{ position: "absolute", right: "10px", top: "10px" }}>
                            <InfoIcon fill={colorPrimary()} width={"15px"} height={"15px"} />
                        </Tooltip>
                        <p className="table-header-dashboard-title">{t('price_paid')}</p>
                    </div>
                    <div style={{ ...cellStyle, }} className="table-header-dashboard">
                        <p className="table-header-dashboard-title">{t('ppunch_paid')}</p>
                    </div>
                    {/* <div style={{ ...cellStyle, position: "relative" }} className="table-header-dashboard">
                        <Tooltip title={t('info_report_traceable')} style={{ position: "absolute", right: "10px", top: "10px" }}>
                            <InfoIcon fill={colorPrimary()} width={"15px"} height={"15px"} />
                        </Tooltip>
                        <p className="table-header-dashboard-title">{t('report_traceable_column')}</p>
                    </div> */}
                    <div style={{ ...cellStyle }} className="table-header-dashboard">
                        <p className="table-header-dashboard-title">{t('traceability')}</p>
                    </div>
                </div>
                {contractsParam.map((c, i) => <ContractItem key={i} contract={c} />)}
            </div>
        )
    }

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight) {
                setPaginationLoading(true)
                nbContractDisplayed.current += Pagination
                setContractToDisplay(contractsRef.current.slice(0, nbContractDisplayed.current))
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <div id="tata" className="greenPaleBgColor" style={{ height: "100%", padding: "30px 10px", flex: 1 }}>
            <Grid container>
                <Grid item xs={12}>
                    <ReportBarSummary contracts={contracts} />
                </Grid>
                <Grid item xs={12}>
                    <p className="table-label-indicator">{`${contracts.length} ${t('contracts')}`}</p>
                </Grid>
                <ContractTable contractsParam={contractToDisplay} />
            </Grid>
            <div style={{ display: "flex", justifyContent: "centre", position: "relative", height: paginationLoading ? '120px' : 0 }}>
                <Loader fullScreen={false} isLoading={paginationLoading} />
            </div>
        </div>
    )
}